export const GROSS_BILLABLE_AMOUNT_BASIS = "gross_billable";

export interface GrossBillableOverviewItem {
  period: string;
  totalCost: number;
}

export interface GrossBillableBreakdown {
  base: number;
  view: number;
  access: number;
  negotiate: number;
}

const finiteNonNegative = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

// The labels in the dashboard explicitly promise advertiser gross billable
// amounts. Reject an absent/different basis or malformed amount instead of
// silently relabelling another financial projection or inventing zero.
export function parseGrossBillableOverview(payload: unknown): GrossBillableOverviewItem[] {
  if (!Array.isArray(payload)) throw new Error("Invalid dashboard cost overview response");
  return payload.map((item) => {
    const projection = item as Record<string, unknown> | null;
    if (
      !projection ||
      typeof projection !== "object" ||
      projection.amountBasis !== GROSS_BILLABLE_AMOUNT_BASIS ||
      typeof projection.period !== "string" ||
      !finiteNonNegative(projection.totalCost)
    ) {
      throw new Error("Dashboard cost overview is not a gross billable projection");
    }
    return { period: projection.period, totalCost: projection.totalCost };
  });
}

export function parseGrossBillableBreakdown(payload: unknown): GrossBillableBreakdown {
  if (
    !payload ||
    typeof payload !== "object" ||
    (payload as Record<string, unknown>).amountBasis !== GROSS_BILLABLE_AMOUNT_BASIS
  ) {
    throw new Error("Dashboard cost breakdown is not a gross billable projection");
  }

  const projection = payload as Record<string, unknown>;
  for (const key of ["base", "view", "access", "negotiate"] as const) {
    if (!finiteNonNegative(projection[key])) {
      throw new Error(`Invalid gross billable ${key} amount`);
    }
  }
  return {
    base: projection.base as number,
    view: projection.view as number,
    access: projection.access as number,
    negotiate: projection.negotiate as number,
  };
}
